/**
 * The PREBUILT catalog store — the free front door's data. Lists the ready-made
 * catalog companions (`Aria` / `Stella` / `Iris`, seeded under the catalog account)
 * and claims one by cloning her template into the caller's account.
 *
 * Claiming is FREE by construction: the edge provisions by copying the template's
 * persona + pinned seed + reference + pre-generated gallery — no media job is
 * enqueued and no credit is reserved, which is exactly why this path (and not the
 * custom describe/upload form) is what the "Start free" door must reach. Thin:
 * zero domain logic, zero pricing, the edge is the authority.
 */
import { type ApiError, isErr, type Result } from '@4f/shared';
import { createSignal } from 'solid-js';
import type { ApiClient } from '../api/client.ts';
import type { CompanionView, PrebuiltTemplateView } from '../api/types.ts';

export interface PrebuiltStore {
  readonly templates: () => readonly PrebuiltTemplateView[];
  readonly loading: () => boolean;
  readonly error: () => ApiError | null;
  /** The template id currently being claimed, or `null`. */
  readonly claiming: () => string | null;
  /** Fetch (or re-fetch) the catalog. */
  load(): Promise<void>;
  /**
   * Clone a template into the account. Returns the finished companion on `ok`,
   * the edge's `err` otherwise, or `null` when a claim is already in flight (a
   * double-tap must never provision two companions).
   */
  claim(templateId: string): Promise<Result<CompanionView, ApiError> | null>;
}

export function createPrebuiltStore(api: ApiClient): PrebuiltStore {
  const [templates, setTemplates] = createSignal<readonly PrebuiltTemplateView[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal<ApiError | null>(null);
  const [claiming, setClaiming] = createSignal<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.listPrebuiltCatalog();
      if (isErr(result)) {
        setError(result.error);
        return;
      }
      setTemplates(result.value);
    } finally {
      setLoading(false);
    }
  };

  const claim = async (templateId: string): Promise<Result<CompanionView, ApiError> | null> => {
    if (claiming()) {
      return null;
    }
    setClaiming(templateId);
    setError(null);
    try {
      const result = await api.provisionPrebuilt(templateId);
      if (isErr(result)) {
        setError(result.error);
      }
      return result;
    } finally {
      setClaiming(null);
    }
  };

  return { templates, loading, error, claiming, load, claim };
}
